import apiClient, { setToken, removeToken, getToken } from './client';

/**
 * Auth API endpoints
 * 
 * Uses the dedicated admin sync events authentication endpoints.
 * Access is restricted to users with is_staff=true, is_superuser=true, or a
 * scoped admin_role such as 'support'.
 */

// Base path for admin sync events API
const ADMIN_PATH = '/admin/sync-events';

export interface LoginCredentials {
  email: string;
  password: string;
}

export type AdminRole = 'none' | 'support' | 'full';

export interface AuthUser {
  id: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
  admin_role?: AdminRole;
}

/**
 * A support user is scoped to the User Accounts section only.
 *
 * This drives navigation and button visibility for UX purposes only — the
 * backend enforces the same scope independently, so a support user who edits
 * localStorage or types a URL directly still gets a 403 from the API.
 */
export function isSupportOnly(user: AuthUser | null): boolean {
  if (!user) return false;
  return user.admin_role === 'support' && !user.is_staff && !user.is_superuser;
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
}

/**
 * Login with email and password
 * POST /api/admin/sync-events/login/
 */
export async function login(credentials: LoginCredentials): Promise<AuthUser> {
  const response = await apiClient.post<LoginResponse>(`${ADMIN_PATH}/login/`, credentials);
  
  const { token, user } = response.data;
  
  // Store the token
  setToken(token);
  // Store user info for session persistence
  storeUser(user);
  
  return user;
}

/**
 * Logout - invalidate token on server
 * POST /api/admin/sync-events/logout/
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post(`${ADMIN_PATH}/logout/`);
  } catch (error) {
    console.error('[Auth] Logout error:', error);
  } finally {
    removeToken();
    clearUser();
  }
}

/**
 * Get current user info / verify session
 * GET /api/admin/sync-events/me/
 */
export async function getCurrentUser(): Promise<AuthUser> {
  const response = await apiClient.get<AuthUser>(`${ADMIN_PATH}/me/`);
  return response.data;
}

/**
 * Store user info in localStorage
 */
export function storeUser(user: AuthUser): void {
  localStorage.setItem('adminUser', JSON.stringify(user));
}

/**
 * Get stored user info
 */
export function getStoredUser(): AuthUser | null {
  const stored = localStorage.getItem('adminUser');
  if (!stored) return null;
  try {
    return JSON.parse(stored) as AuthUser;
  } catch {
    return null;
  }
}

/**
 * Clear stored user info
 */
export function clearUser(): void {
  localStorage.removeItem('adminUser');
}

/**
 * Check if user is authenticated
 * Validates token with the server
 */
export async function checkAuth(): Promise<AuthUser | null> {
  const token = getToken();
  if (!token) return null;
  
  try {
    // Verify token is still valid with server
    const user = await getCurrentUser();
    storeUser(user); // Update stored user info
    return user;
  } catch {
    // Token invalid - clear everything
    removeToken();
    clearUser();
    return null;
  }
}

export default {
  login,
  logout,
  getCurrentUser,
  checkAuth,
  storeUser,
  getStoredUser,
  clearUser,
  isSupportOnly,
};
